import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import EditNoteIcon from "@mui/icons-material/EditNote";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import AppDialog from "../General/Dialog/AppDialog";
import TemplateEditor from "../General/TemplateEditor";
import { updateAttachmentType } from "../../services/AttachmentTypeService";
import { useToast } from "../../context/ToastContext";

const BOOLEAN_FIELDS = [
  { key: "allow_upload",                     label: "Permitir subida manual",        description: "Habilita que el usuario suba un archivo manualmente." },
  { key: "allow_multiple",                   label: "Permitir múltiples envíos",      description: "Permite enviar el mismo escrito más de una vez al mismo expediente." },
  { key: "allow_generate",                   label: "Permitir generación",            description: "Habilita la generación automática del escrito." },
  { key: "allow_online_upload",              label: "Permitir subida online",         description: "Permite subir el escrito directamente al portal judicial." },
  { key: "requires_file",                    label: "Requiere archivo",               description: "El escrito necesita un archivo físico adjunto para enviarse." },
  { key: "uses_lawsuit_file",                label: "Usa archivo de título",          description: "Usa el PDF del título como archivo principal del escrito." },
  { key: "use_for_notification_certificate", label: "Usar para cédulas",              description: "Habilita este tipo en el flujo de cédulas de notificación." },
  { key: "status",                           label: "Activo",                         description: "El tipo de escrito está habilitado y visible.", isBoolFromStatus: true },
];

function buildForm(type) {
  return {
    name:                             type?.name                              ?? "",
    display_name:                     type?.display_name                      ?? "",
    id_web:                           type?.id_web                            ?? "",
    text:                             type?.text                              ?? "",
    generate_title:                   type?.generate_title                    ?? "",
    generate_template:                type?.generate_template                 ?? "",
    generate_engine:                  type?.generate_engine                   ?? "",
    allow_upload:                     type?.allow_upload                      ?? false,
    allow_multiple:                   type?.allow_multiple                    ?? false,
    allow_generate:                   type?.allow_generate                    ?? false,
    allow_online_upload:              type?.allow_online_upload               ?? false,
    requires_file:                    type?.requires_file                     ?? true,
    uses_lawsuit_file:                type?.uses_lawsuit_file                 ?? false,
    use_for_notification_certificate: type?.use_for_notification_certificate  ?? false,
    status:                           type?.status === "enabled"              ?? true,
  };
}

export default function AttachmentTypeEditDialog({ open, onClose, attachmentType }) {
  const { showToast } = useToast();
  const [form, setForm]     = useState(() => buildForm(attachmentType));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildForm(attachmentType));
  }, [open, attachmentType]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: form.status ? "enabled" : "disabled",
      };
      await updateAttachmentType(attachmentType.id, payload);
      showToast("Tipo de escrito actualizado.", "success");
      onClose(true);
    } catch {
      showToast("Error al guardar los cambios.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={() => onClose(false)}
      title={attachmentType?.display_name || attachmentType?.name || "Tipo de escrito"}
      subtitle="Configuración del tipo de escrito"
      icon={<EditNoteIcon />}
      maxWidth="md"
      fullWidth
      dividers
      actions={
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Guardar
        </Button>
      }
    >
      <Stack spacing={3} sx={{ py: 1 }}>

        {/* Datos básicos */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            Datos básicos
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Nombre portal (name)"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                size="small"
                fullWidth
                helperText="Nombre que aparece en el portal SAC. Debe coincidir exactamente."
              />
              <TextField
                label="Nombre para mostrar"
                value={form.display_name}
                onChange={(e) => set("display_name", e.target.value)}
                size="small"
                fullWidth
                helperText="Nombre visible en la interfaz del sistema."
              />
            </Stack>
          </Stack>
        </Box>

        {/* Opciones booleanas */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            Opciones
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0.5}>
            {BOOLEAN_FIELDS.map(({ key, label, description, isBoolFromStatus }) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">{description}</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form[key])}
                      onChange={(e) => set(key, e.target.checked)}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Texto del escrito */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
              Texto del escrito
            </Typography>
            <Chip label="[CARATULA] [EXPEDIENTE] [Quien_Suscribe] [REEMPLAZAR]" size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
            <Tooltip title="Se completan con los datos leídos de la Designación de Martillero cargada en el expediente. Si falta el dato, ese expediente no se envía.">
              <Chip label="[MARTILLERO] [MATRICULA_MARTILLERO]" size="small" variant="outlined" color="secondary" sx={{ fontSize: 10, height: 20 }} />
            </Tooltip>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <TemplateEditor
            value={form.text}
            onChange={(v) => set("text", v)}
          />
        </Box>

        {/* Plantilla de generación */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            Plantilla de generación (HTML)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Título del documento generado"
                value={form.generate_title}
                onChange={(e) => set("generate_title", e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Motor de generación"
                value={form.generate_engine}
                onChange={(e) => set("generate_engine", e.target.value)}
                size="small"
                sx={{ maxWidth: 180 }}
                helperText="ej: dompdf"
              />
            </Stack>
            <TemplateEditor
              value={form.generate_template}
              onChange={(v) => set("generate_template", v)}
            />
          </Stack>
        </Box>

      </Stack>
    </AppDialog>
  );
}
